import {
  Box,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Textarea,
  Tooltip,
} from '@chakra-ui/react'
import { ChangeEvent, useState } from 'react'
import { PossibleDataTypes } from '../../utils/pageDrawerInputLists/inputLists'
import CheckboxGroupSearch from './checkboxGroupSearch'

export type SelectOptionProps = {
  text: string
  value: string
}

export type RadioOptionProps = {
  text: string
  value: string
  component: JSX.Element
}

export type FormElementsType = {
  type: string
  value:
    | string
    | number
    | (string | number)[]
    | boolean
    | PossibleDataTypes
    | PossibleDataTypes[]
    | undefined
  name: string
  label: string
  onFormChange?: (
    e:
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
      | string
      | (string | number)[],
    name?: string,
  ) => void
  required?: boolean
  helperText?: string
  selectOptions?: SelectOptionProps[]
  checkboxOptions?: SelectOptionProps[]
  radioOptions?: RadioOptionProps[]
}

const hexCodeRegexPattern = /^#([A-Fa-f0-9]{6})$/,
  hexCodeRegex = new RegExp(hexCodeRegexPattern)

const FormElements = ({
  type,
  name,
  value = '',
  required = false,
  label,
  helperText = '',
  selectOptions = [],
  checkboxOptions = [],
  radioOptions = [],
  onFormChange,
}: FormElementsType) => {
  const [touched, setTouched] = useState(false)
  let element = <Input />
  if (Array.isArray(value)) {
    element = (
      <CheckboxGroupSearch
        onFormChange={onFormChange}
        value={value as (string | number)[]}
        checkboxOptions={checkboxOptions}
      />
    )
  } else {
    if (type === 'text' || type === 'email' || type === 'number') {
      element = (
        <Input
          type={type}
          name={name}
          value={value as string | number}
          onChange={onFormChange}
        />
      )
    } else if (type === 'year') {
      element = (
        <Input
          type={'number'}
          min={1500}
          max={2099}
          name={name}
          value={value as number}
          onChange={onFormChange}
        />
      )
    } else if (type === 'patternText') {
      element = (
        <Box position={'relative'}>
          <Input
            type={type}
            name={name}
            value={value as string}
            onChange={onFormChange}
            onBlur={() => setTouched(true)}
          />
          <Tooltip label="Color Preview" hasArrow placement="top">
            <Box
              width={30}
              height={30}
              bg={`${value}`}
              position={'absolute'}
              top={'5px'}
              right={'8px'}
              cursor={'pointer'}
            />
          </Tooltip>
        </Box>
      )
    } else if (type === 'textarea') {
      element = (
        <Textarea name={name} value={value as string} onChange={onFormChange} />
      )
    } else if (type === 'select') {
      element = (
        <Select onChange={onFormChange} name={name} value={value as string}>
          <option value={''}>-</option>
          {selectOptions.map((option: SelectOptionProps, i: number) => (
            <option key={i} value={option.value}>
              {option.text}
            </option>
          ))}
        </Select>
      )
    } else if (type === 'imageRadio') {
      element = (
        <RadioGroup
          onChange={(e) => onFormChange && onFormChange(e, name)}
          name={name}
          value={`${value}`}
        >
          <Stack direction="row">
            {radioOptions.map((option: RadioOptionProps, i: number) => (
              <Radio key={i} value={option.value} border={'2px solid black'}>
                {option.text}
                {option.component}
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      )
    }
  }

  const isError = type === 'patternText' && !hexCodeRegex.test(`${value}`)

  return (
    <FormControl isRequired={required} isInvalid={isError && touched}>
      <FormLabel textTransform={'capitalize'}>{label}</FormLabel>
      {element}
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      {isError && touched && (
        <FormErrorMessage>
          Fill in the hexcode correctly. Eg. '#abcdef'
        </FormErrorMessage>
      )}
    </FormControl>
  )
}

export default FormElements
